/**
 * AcceptedPlanScreen
 *
 * Captures the treatment plan the patient has actually accepted with their
 * clinic. This is intentionally separate from the modalities they are still
 * considering in ModalityScreen.
 */

import React from "react";
import { ScrollView, Text, Pressable, View } from "react-native";
import {
  Check,
  Dna,
  Egg,
  FlaskConical,
  Snowflake,
  Syringe,
  Users,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import WarmButton from "@louise/moon-ui/primitives/WarmButton";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { usePlanBuilder } from "../PlanBuilderContext";
import type { AcceptedTreatmentPlanDraft } from "../../treatmentPlanTypes";

type Props = {
  onNext: () => void;
};

type ChoiceOption<T extends string> = {
  id: T;
  labelKey: string;
  detailKey: string;
};

type ChoiceSectionProps<T extends string> = {
  title: string;
  options: readonly ChoiceOption<T>[];
  selected: T;
  onSelect: (value: T) => void;
  Icon: LucideIcon;
};

type TreatmentFamily = AcceptedTreatmentPlanDraft["treatmentFamily"];
type FertilizationMethod = AcceptedTreatmentPlanDraft["fertilizationMethod"];
type EggSource = AcceptedTreatmentPlanDraft["eggSource"];
type EggState = AcceptedTreatmentPlanDraft["eggState"];
type SpermSource = AcceptedTreatmentPlanDraft["spermSource"];
type SpermState = AcceptedTreatmentPlanDraft["spermState"];
type TransferPlan = AcceptedTreatmentPlanDraft["transferPlan"];
type PgtPlan = AcceptedTreatmentPlanDraft["pgtPlan"];
type DecisionSource = AcceptedTreatmentPlanDraft["decisionSource"];

const TREATMENT_OPTIONS = [
  {
    id: "ivf",
    labelKey: "plan_builder.accepted_plan.ivf_option",
    detailKey: "plan_builder.accepted_plan.ivf_detail",
  },
  {
    id: "iui",
    labelKey: "plan_builder.accepted_plan.iui_option",
    detailKey: "plan_builder.accepted_plan.iui_detail",
  },
  {
    id: "egg_freezing",
    labelKey: "plan_builder.accepted_plan.egg_freezing_option",
    detailKey: "plan_builder.accepted_plan.egg_freezing_detail",
  },
  {
    id: "embryo_freezing",
    labelKey: "plan_builder.accepted_plan.embryo_freezing_option",
    detailKey: "plan_builder.accepted_plan.embryo_freezing_detail",
  },
  {
    id: "sperm_freezing",
    labelKey: "plan_builder.accepted_plan.sperm_freezing_option",
    detailKey: "plan_builder.accepted_plan.sperm_freezing_detail",
  },
  {
    id: "fet",
    labelKey: "plan_builder.accepted_plan.fet_option",
    detailKey: "plan_builder.accepted_plan.fet_detail",
  },
  {
    id: "undecided",
    labelKey: "plan_builder.accepted_plan.not_decided_option",
    detailKey: "plan_builder.accepted_plan.not_decided_detail",
  },
] satisfies readonly ChoiceOption<TreatmentFamily>[];

const FERTILIZATION_OPTIONS = [
  {
    id: "conventional_ivf",
    labelKey: "plan_builder.accepted_plan.conventional_ivf_option",
    detailKey: "plan_builder.accepted_plan.conventional_ivf_detail",
  },
  {
    id: "icsi",
    labelKey: "plan_builder.accepted_plan.icsi_option",
    detailKey: "plan_builder.accepted_plan.icsi_detail",
  },
  {
    id: "split_ivf_icsi",
    labelKey: "plan_builder.accepted_plan.split_ivf_icsi_option",
    detailKey: "plan_builder.accepted_plan.split_ivf_icsi_detail",
  },
  {
    id: "rescue_icsi",
    labelKey: "plan_builder.accepted_plan.rescue_icsi_option",
    detailKey: "plan_builder.accepted_plan.rescue_icsi_detail",
  },
  {
    id: "not_applicable",
    labelKey: "plan_builder.accepted_plan.not_applicable_option",
    detailKey: "plan_builder.accepted_plan.not_applicable_detail",
  },
  {
    id: "undecided",
    labelKey: "plan_builder.accepted_plan.not_decided_option",
    detailKey: "plan_builder.accepted_plan.not_decided_detail",
  },
] satisfies readonly ChoiceOption<FertilizationMethod>[];

const EGG_SOURCE_OPTIONS = [
  {
    id: "self",
    labelKey: "plan_builder.accepted_plan.self_source_option",
    detailKey: "plan_builder.accepted_plan.self_source_detail",
  },
  {
    id: "partner",
    labelKey: "plan_builder.accepted_plan.partner_source_option",
    detailKey: "plan_builder.accepted_plan.partner_source_detail",
  },
  {
    id: "donor",
    labelKey: "plan_builder.accepted_plan.donor_source_option",
    detailKey: "plan_builder.accepted_plan.donor_source_detail",
  },
  {
    id: "external",
    labelKey: "plan_builder.accepted_plan.external_source_option",
    detailKey: "plan_builder.accepted_plan.external_source_detail",
  },
  {
    id: "not_applicable",
    labelKey: "plan_builder.accepted_plan.not_applicable_option",
    detailKey: "plan_builder.accepted_plan.not_applicable_detail",
  },
  {
    id: "undecided",
    labelKey: "plan_builder.accepted_plan.not_decided_option",
    detailKey: "plan_builder.accepted_plan.not_decided_detail",
  },
] satisfies readonly ChoiceOption<EggSource>[];

const EGG_STATE_OPTIONS = [
  {
    id: "fresh",
    labelKey: "plan_builder.accepted_plan.fresh_state_option",
    detailKey: "plan_builder.accepted_plan.fresh_state_detail",
  },
  {
    id: "frozen",
    labelKey: "plan_builder.accepted_plan.frozen_state_option",
    detailKey: "plan_builder.accepted_plan.frozen_state_detail",
  },
  {
    id: "not_applicable",
    labelKey: "plan_builder.accepted_plan.not_applicable_option",
    detailKey: "plan_builder.accepted_plan.not_applicable_detail",
  },
  {
    id: "undecided",
    labelKey: "plan_builder.accepted_plan.not_decided_option",
    detailKey: "plan_builder.accepted_plan.not_decided_detail",
  },
] satisfies readonly ChoiceOption<EggState>[];

const SPERM_SOURCE_OPTIONS: readonly ChoiceOption<SpermSource>[] =
  EGG_SOURCE_OPTIONS;

const SPERM_STATE_OPTIONS = [
  {
    id: "fresh",
    labelKey: "plan_builder.accepted_plan.fresh_state_option",
    detailKey: "plan_builder.accepted_plan.fresh_state_detail",
  },
  {
    id: "frozen",
    labelKey: "plan_builder.accepted_plan.frozen_state_option",
    detailKey: "plan_builder.accepted_plan.frozen_state_detail",
  },
  {
    id: "surgically_retrieved",
    labelKey: "plan_builder.accepted_plan.surgically_retrieved_option",
    detailKey: "plan_builder.accepted_plan.surgically_retrieved_detail",
  },
  {
    id: "not_applicable",
    labelKey: "plan_builder.accepted_plan.not_applicable_option",
    detailKey: "plan_builder.accepted_plan.not_applicable_detail",
  },
  {
    id: "undecided",
    labelKey: "plan_builder.accepted_plan.not_decided_option",
    detailKey: "plan_builder.accepted_plan.not_decided_detail",
  },
] satisfies readonly ChoiceOption<SpermState>[];

const TRANSFER_OPTIONS = [
  {
    id: "fresh_transfer",
    labelKey: "plan_builder.accepted_plan.fresh_transfer_option",
    detailKey: "plan_builder.accepted_plan.fresh_transfer_detail",
  },
  {
    id: "freeze_all",
    labelKey: "plan_builder.accepted_plan.freeze_all_option",
    detailKey: "plan_builder.accepted_plan.freeze_all_detail",
  },
  {
    id: "later_fet",
    labelKey: "plan_builder.accepted_plan.later_fet_option",
    detailKey: "plan_builder.accepted_plan.later_fet_detail",
  },
  {
    id: "no_transfer",
    labelKey: "plan_builder.accepted_plan.no_transfer_option",
    detailKey: "plan_builder.accepted_plan.no_transfer_detail",
  },
  {
    id: "undecided",
    labelKey: "plan_builder.accepted_plan.not_decided_option",
    detailKey: "plan_builder.accepted_plan.not_decided_detail",
  },
] satisfies readonly ChoiceOption<TransferPlan>[];

const PGT_OPTIONS = [
  {
    id: "pgt_a",
    labelKey: "plan_builder.accepted_plan.pgt_a_option",
    detailKey: "plan_builder.accepted_plan.pgt_a_detail",
  },
  {
    id: "pgt_m",
    labelKey: "plan_builder.accepted_plan.pgt_m_option",
    detailKey: "plan_builder.accepted_plan.pgt_m_detail",
  },
  {
    id: "pgt_sr",
    labelKey: "plan_builder.accepted_plan.pgt_sr_option",
    detailKey: "plan_builder.accepted_plan.pgt_sr_detail",
  },
  {
    id: "other",
    labelKey: "plan_builder.accepted_plan.other_pgt_option",
    detailKey: "plan_builder.accepted_plan.other_pgt_detail",
  },
  {
    id: "not_planned",
    labelKey: "plan_builder.accepted_plan.not_planned_option",
    detailKey: "plan_builder.accepted_plan.not_planned_detail",
  },
  {
    id: "undecided",
    labelKey: "plan_builder.accepted_plan.not_decided_option",
    detailKey: "plan_builder.accepted_plan.not_decided_detail",
  },
] satisfies readonly ChoiceOption<PgtPlan>[];

const DECISION_SOURCE_OPTIONS = [
  {
    id: "patient",
    labelKey: "plan_builder.accepted_plan.patient_decision_option",
    detailKey: "plan_builder.accepted_plan.patient_decision_detail",
  },
  {
    id: "clinic",
    labelKey: "plan_builder.accepted_plan.clinic_decision_option",
    detailKey: "plan_builder.accepted_plan.clinic_decision_detail",
  },
  {
    id: "patient_and_clinic",
    labelKey: "plan_builder.accepted_plan.shared_decision_option",
    detailKey: "plan_builder.accepted_plan.shared_decision_detail",
  },
  {
    id: "undecided",
    labelKey: "plan_builder.accepted_plan.not_decided_option",
    detailKey: "plan_builder.accepted_plan.not_decided_detail",
  },
] satisfies readonly ChoiceOption<DecisionSource>[];

function ChoiceSection<T extends string>({
  title,
  options,
  selected,
  onSelect,
  Icon,
}: ChoiceSectionProps<T>) {
  const t = useTranslations();
  const capturePress = usePressCapture("ChoiceSection");

  return (
    <View style={{ marginBottom: 24 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <Icon size={18} color={EMBER.colors.warm} strokeWidth={1.8} />
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.text,
            fontSize: 16,
            fontWeight: "600",
          }}
        >
          {title}
        </Text>
      </View>
      <View style={{ gap: 8 }}>
        {options.map((option) => {
          const isSelected = selected === option.id;
          return (
            <Pressable
              key={option.id}
              onPress={() => {
                capturePress(pressLabel(t(option.labelKey)), option.id);
                onSelect(option.id);
              }}
              accessibilityRole="button"
              accessibilityState={{ selected: isSelected }}
              aria-selected={isSelected}
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
                paddingHorizontal: 14,
                paddingVertical: 12,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: isSelected
                  ? EMBER.colors.text
                  : "rgba(255, 255, 255, 0.9)",
                backgroundColor: isSelected
                  ? `${EMBER.colors.text}10`
                  : "rgba(255, 255, 255, 0.78)",
              }}
            >
              <View style={{ flex: 1 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    color: EMBER.colors.text,
                    fontSize: 14,
                    fontWeight: isSelected ? "600" : "500",
                  }}
                >
                  {t(option.labelKey)}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    color: EMBER.colors.textLight,
                    fontSize: 12,
                    lineHeight: 17,
                    marginTop: 2,
                  }}
                >
                  {t(option.detailKey)}
                </Text>
              </View>
              <View
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 11,
                  borderWidth: 2,
                  borderColor: isSelected
                    ? EMBER.colors.text
                    : EMBER.colors.border,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isSelected && (
                  <Check size={13} color={EMBER.colors.text} strokeWidth={3} />
                )}
              </View>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

export default function AcceptedPlanScreen({ onNext }: Props) {
  const t = useTranslations();
  const { state, updateState } = usePlanBuilder();

  const updatePlan = (patch: Partial<AcceptedTreatmentPlanDraft>) => {
    updateState({
      acceptedPlan: { ...state.acceptedPlan, ...patch },
    });
  };

  return (
    <ScrollView
      testID="plan-builder-accepted-plan-screen"
      contentContainerStyle={{ padding: 24, paddingBottom: 40 }}
      showsVerticalScrollIndicator={false}
    >
      <Text
        className="font-dm-sans"
        style={{
          color: EMBER.colors.text,
          fontSize: 22,
          fontWeight: "700",
          lineHeight: 28,
          marginBottom: 8,
        }}
      >
        {t("plan_builder.accepted_plan.current_plan_title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          color: EMBER.colors.textLight,
          fontSize: 15,
          lineHeight: 22,
          marginBottom: 24,
        }}
      >
        {t("plan_builder.accepted_plan.current_plan_subtitle")}
      </Text>

      <View
        style={{
          flexDirection: "row",
          gap: 10,
          alignItems: "flex-start",
          borderRadius: 12,
          backgroundColor: `${EMBER.colors.warm}10`,
          padding: 14,
          marginBottom: 24,
        }}
      >
        <Users size={18} color={EMBER.colors.warm} strokeWidth={1.8} />
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.textMid,
            flex: 1,
            fontSize: 13,
            lineHeight: 19,
          }}
        >
          {t("plan_builder.accepted_plan.distinction_note")}
        </Text>
      </View>

      <ChoiceSection
        title={t("plan_builder.accepted_plan.treatment_family_prompt")}
        options={TREATMENT_OPTIONS}
        selected={state.acceptedPlan.treatmentFamily}
        onSelect={(treatmentFamily) => updatePlan({ treatmentFamily })}
        Icon={Syringe}
      />
      <ChoiceSection
        title={t("plan_builder.accepted_plan.fertilization_method_prompt")}
        options={FERTILIZATION_OPTIONS}
        selected={state.acceptedPlan.fertilizationMethod}
        onSelect={(fertilizationMethod) => updatePlan({ fertilizationMethod })}
        Icon={FlaskConical}
      />
      <ChoiceSection
        title={t("plan_builder.accepted_plan.egg_source_prompt")}
        options={EGG_SOURCE_OPTIONS}
        selected={state.acceptedPlan.eggSource}
        onSelect={(eggSource) => updatePlan({ eggSource })}
        Icon={Egg}
      />
      <ChoiceSection
        title={t("plan_builder.accepted_plan.egg_state_prompt")}
        options={EGG_STATE_OPTIONS}
        selected={state.acceptedPlan.eggState}
        onSelect={(eggState) => updatePlan({ eggState })}
        Icon={Snowflake}
      />
      <ChoiceSection
        title={t("plan_builder.accepted_plan.sperm_source_prompt")}
        options={SPERM_SOURCE_OPTIONS}
        selected={state.acceptedPlan.spermSource}
        onSelect={(spermSource) => updatePlan({ spermSource })}
        Icon={Dna}
      />
      <ChoiceSection
        title={t("plan_builder.accepted_plan.sperm_state_prompt")}
        options={SPERM_STATE_OPTIONS}
        selected={state.acceptedPlan.spermState}
        onSelect={(spermState) => updatePlan({ spermState })}
        Icon={Snowflake}
      />
      <ChoiceSection
        title={t("plan_builder.accepted_plan.transfer_plan_prompt")}
        options={TRANSFER_OPTIONS}
        selected={state.acceptedPlan.transferPlan}
        onSelect={(transferPlan) => updatePlan({ transferPlan })}
        Icon={Syringe}
      />
      <ChoiceSection
        title={t("plan_builder.accepted_plan.pgt_plan_prompt")}
        options={PGT_OPTIONS}
        selected={state.acceptedPlan.pgtPlan}
        onSelect={(pgtPlan) => updatePlan({ pgtPlan })}
        Icon={Dna}
      />
      <ChoiceSection
        title={t("plan_builder.accepted_plan.decision_source_prompt")}
        options={DECISION_SOURCE_OPTIONS}
        selected={state.acceptedPlan.decisionSource}
        onSelect={(decisionSource) => updatePlan({ decisionSource })}
        Icon={Users}
      />

      <WarmButton
        title={t("plan_builder.accepted_plan.continue_with_plan")}
        onPress={() => {
          updateState({ acceptedPlanAcknowledged: true });
          onNext();
        }}
      />
    </ScrollView>
  );
}
